import { useCallback, useMemo } from 'react'

import { BuilderMenuItem, DefaultVariableResult, VariableSelectFeatureFlag } from '@makyoapp/makyo-common'
import { Button, ColorVariant, DropDownMenu, MaterialIcon, useRtl } from '@makyoapp/makyo-tw-ui'

import { parseVariableSelect } from '@/core/app-builder/parser/variable-select'
import { parseVariableTextSingleCell } from '@/core/app-builder/parser/variable-text'
import { isDescendant, parseStringToDescendant } from '@/lib/slate'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useBuilderActionContextV2 } from '@/context/__project__/BuilderActionContextV2'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { checkVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { THUMB_AVATAR } from '@/shared/constants'

interface CompMenuV2Props {
  menuId: string
  backgroundColor: ColorVariant
  triggerButtonColor: ColorVariant
  tableContext?: {
    tableId?: string
    rowIndex?: number
    rowId?: string
  }
}

/**
 * New Menu Component that can be used as addons to any component.
 *
 * @example - see comment wrapper
 */
export const CompMenuV2 = ({ menuId, backgroundColor, triggerButtonColor, tableContext }: CompMenuV2Props) => {
  const dir = useRtl()
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { currentUser } = useAppAuthContext()
  const { simulateAction } = useBuilderActionContextV2()
  const { menus } = useAppBuilderContext()
  const { activeLayerTableId, activeLayerDetailRowIndex, activeLayerDetailRowId } = useActiveLayerContext()
  const { device } = useBreakpoint()

  const menu = menus.find(({ id }) => id === menuId)

  const tableId = tableContext?.tableId ?? activeLayerTableId
  const rowIndex = tableContext?.rowIndex ?? activeLayerDetailRowIndex
  const rowId = tableContext?.rowId ?? activeLayerDetailRowId ?? ''

  // Check if the menu should be shown
  const shouldShowMenu = checkVisibilityV2(
    menu?.visibility,
    dbClient,
    currentUser?.email || '',
    language || 'en',
    userSpecific || '',
    tableId,
    rowIndex,
    undefined,
    device
  )

  // Function to get badge content for any menu item
  const getBadgeContent = useCallback(
    (menuItem: BuilderMenuItem) => {
      // Get the badge size from the menu item, default to 's' if not provided
      const badgeSize = menuItem.badgeSize || 's'

      // Parse the badge value directly with parseVariableSelect
      const badgeResult = parseVariableSelect(
        menuItem.badgeMetadata as DefaultVariableResult,
        dbClient,
        currentUser?.email || '',
        language,
        userSpecific,
        tableId,
        rowId
      )

      const badge = badgeResult?.[0] as string | undefined

      // Determine if badge should be shown
      const showBadge = !!badge

      // For medium badges, show the value. For small badges, show empty string
      const badgeValue = badgeSize === 'm' ? badge : ''

      return {
        badge,
        badgeSize,
        badgeValue,
        showBadge,
      }
    },
    [dbClient, currentUser?.email, language, userSpecific, tableId, rowId]
  )

  // Local function to parse variable text without using hooks - mimics useVariableTextSingleCell logic
  const parseVariableTextLocal = useCallback(
    (value = '') => {
      if (isDescendant(value) && !!dbClient) {
        const node = parseStringToDescendant(value)
        return parseVariableTextSingleCell(node, dbClient, language, userSpecific, currentUser?.email)
      }
      return value
    },
    [dbClient, language, userSpecific, currentUser?.email]
  )

  const menuItems = useMemo(() => {
    if (!menu || !shouldShowMenu) return []

    const isActionButtonsHaveIconOrImage = menu?.items.some((m) => {
      const isIcon = m?.icon?.flag === VariableSelectFeatureFlag.Icon
      const isAvatar = m?.icon?.flag === VariableSelectFeatureFlag.Avatar
      const isImage = m?.icon?.flag === VariableSelectFeatureFlag.Image
      return isIcon || isAvatar || isImage
    })

    return (menu?.items || [])
      .filter((menuItem) => {
        const shouldShow = checkVisibilityV2(
          menuItem.visibility,
          dbClient,
          currentUser?.email || '',
          language || 'en',
          userSpecific || '',
          tableId,
          rowIndex,
          undefined,
          device
        )

        return shouldShow
      })
      .map((menuItem) => {
        const isIcon = menuItem?.icon?.flag === VariableSelectFeatureFlag.Icon
        const isAvatar = menuItem?.icon?.flag === VariableSelectFeatureFlag.Avatar
        const isImage = menuItem?.icon?.flag === VariableSelectFeatureFlag.Image

        let media: { icon?: React.ReactNode | undefined; image?: string | undefined } = {
          icon: isActionButtonsHaveIconOrImage ? <div className='w-6 h-6 visible-hidden' /> : undefined,
          image: undefined,
        }

        if (isIcon) {
          media = { icon: <MaterialIcon icon={(menuItem.icon as any)?.value} size={24} /> }
        } else if (isAvatar) {
          media = { image: currentUser?.photo || THUMB_AVATAR, icon: undefined }
        } else if (isImage) {
          media = { image: (menuItem.icon as any)?.value, icon: undefined }
        }

        const badge = getBadgeContent(menuItem)
        let theBadge = undefined

        if (badge.showBadge) {
          if (badge.badgeSize === 's') {
            theBadge = null
          } else {
            theBadge = badge.badgeValue
          }
        }

        // Use the local function to parse the menu item title
        const theLabel = parseVariableTextLocal(menuItem.title)

        return {
          ...media,
          id: menuItem.id,
          label: theLabel,
          badge: theBadge,
          type: 'default' as const,
          onClick: () => {
            if (menuItem.actions && menuItem.actions.length > 0) {
              simulateAction({
                actions: menuItem.actions,
                tableId,
                rowIndex,
                rowId,
              })
            }
          },
        }
      })
  }, [
    menu,
    shouldShowMenu,
    dbClient,
    currentUser?.email,
    currentUser?.photo,
    language,
    userSpecific,
    tableId,
    rowIndex,
    device,
    getBadgeContent,
    parseVariableTextLocal,
    simulateAction,
    rowId,
  ])

  if (menuItems.length === 0) return null

  return (
    <DropDownMenu
      items={menuItems}
      backgroundColor={backgroundColor}
      triggerElement={
        <Button
          backgroundOpacity={0}
          className='flex p-1 items-center justify-center rounded-full hover:bg-black/5 cursor-pointer'
          colorVariant={triggerButtonColor}
          cornerRadius='full'
          size='icon'
          padding={{
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          }}
        >
          <MaterialIcon icon='more_vert' size={24} />
        </Button>
      }
      borderRadius='medium'
      elevation='none'
      isRTL={dir === 'rtl'}
    />
  )
}
